/**
 * The one door. A name, a password, and nothing else on the page.
 *
 * There is no sign-up link and no password reset: ReadCommons has a single
 * account, set in the server's configuration by the person who runs it. A form
 * offering either would be offering something that does not exist.
 */

import { useState, type FormEvent } from "react";

import { ApiError } from "../lib/api";
import { t, useUiLang } from "../i18n";
import { Mark } from "../components/icons";

export function LoginScreen({
  onSubmit,
}: {
  onSubmit: (user: string, password: string) => Promise<void>;
}) {
  useUiLang();
  const [user, setUser] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await onSubmit(user, password);
    } catch (cause) {
      setError(
        cause instanceof ApiError
          ? cause.status === 429
            ? t("authTooMany")
            : t("authWrong")
          : t("errorNoServer"),
      );
      setPassword("");
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center px-5 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center gap-2.5">
          <Mark className="size-fib21 text-[var(--color-accent)]" />
          <span className="text-lg font-semibold tracking-tight">ReadCommons</span>
        </div>

        <h1 className="text-xl font-semibold tracking-tight">{t("authTitle")}</h1>
        <p className="mt-1.5 text-sm text-muted">{t("authLead")}</p>

        <form onSubmit={submit} className="mt-7 flex flex-col gap-3">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium tracking-wide text-faint uppercase">{t("authName")}</span>
            <input
              value={user}
              onChange={(event) => setUser(event.target.value)}
              autoComplete="username"
              autoFocus
              required
              className="min-h-11 rounded-lg border border-line bg-surface px-3 text-ink outline-none transition-colors focus:border-accent"
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium tracking-wide text-faint uppercase">
              {t("authPassword")}
            </span>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              required
              className="min-h-11 rounded-lg border border-line bg-surface px-3 text-ink outline-none transition-colors focus:border-accent"
            />
          </label>

          {/* Reserved whether or not it is filled, so the button does not jump
              under the finger the moment a password is refused. */}
          <p className="min-h-5 text-sm text-accent" role="alert">
            {error}
          </p>

          <button
            type="submit"
            disabled={busy}
            className="min-h-11 rounded-lg bg-accent font-medium text-[var(--color-on-accent)] transition-opacity disabled:opacity-50"
          >
            {busy ? t("authChecking") : t("authSubmit")}
          </button>
        </form>

        <p className="mt-8 text-xs text-faint">
          {t("authLostHint")}{" "}
          <code className="text-muted">python -m readcommons hash-password</code>.
        </p>
      </div>
    </div>
  );
}
